import classNames from "classnames"
import React, { ReactNode, useMemo } from "react"

import { Experiment } from "../../../../constants/experiments"
import { Color } from "../../../../constants/V2/color"
import { useIsExperiment } from "../../../../hooks/experiments"
import { useGetStartedModalStore } from "../../../V2/Sections/GetStarted/store"
import Link from "../../Link"
import Typography, { TypographySize } from "../Typography"

import {
  SharedButtonClickProps,
  handleOnClick,
} from "@utils/buttonEventHandler"
import { getTextColorClass } from "@utils/V2/color"

type TextButtonProps = {
  text: string
  mobileText?: string
  textSize?: TypographySize
  style: "icon" | "underlined" | "text" | "underlined-reverse"
  weight?: "book" | "medium"
  color: Color
  linkUrl?: string
  queryParams?: string
  mobileLinkUrl?: string
  icon?: ReactNode
  disabled?: boolean
  onClick?: () => void
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
  className?: string
  uppercase?: boolean
  "data-testid"?: string
}

const TextButton = ({
  text,
  icon,
  color,
  style,
  weight = "medium",
  onClick,
  linkUrl,
  queryParams,
  mobileText,
  mobileLinkUrl,
  textSize = style === "text" || style === "underlined-reverse"
    ? "subscript-lg"
    : "body-lg",
  trackingEvent,
  trackingEventKey,
  disabled = false,
  trackingEventValue,
  uppercase = style === "text" ? true : false,
  className,
  "data-testid": dataTestId,
}: TextButtonProps) => {
  const sharedButtonClickProps: SharedButtonClickProps = {
    trackingEvent,
    trackingEventKey,
    trackingEventValue,
    onClick,
  }

  const isGetStartedModalExperiment = useIsExperiment(
    Experiment.GET_STARTED_MODAL
  )
  const { setIsGetStartedModalOpen } = useGetStartedModalStore()

  const showGetStartedModal =
    isGetStartedModalExperiment && text.toLowerCase() === "get started"

  const buttonClasses = useMemo(
    () =>
      classNames(
        "group inline-flex py-4 max-w-fit",
        getTextColorClass(color),
        { "opacity-50 pointer-events-none": disabled },
        { "items-center gap-8": style === "icon" },
        {
          "gap-[2px] flex-col after:content-[''] after:block after:bg-current after:h-px after:transition-[width] after:duration-300":
            style === "underlined" ||
            style === "text" ||
            style === "underlined-reverse",
        },
        {
          "after:w-full after:hover:w-[80%] after:focus-visible:w-[80%] items-center":
            style === "underlined",
        },
        {
          "after:w-full after:hover:w-0 after:focus-visible:w-0 items-center":
            style === "underlined-reverse",
        },
        {
          "after:w-0 after:hover:w-full after:focus-visible:w-full items-start":
            style === "text",
        },
        { uppercase: uppercase },
        className
      ),
    [color, disabled, style, uppercase, className]
  )

  const buttonContent = (
    <>
      {mobileText ? (
        <>
          <Typography
            text={mobileText}
            weight={weight}
            font="grotesk"
            color={color}
            size={textSize}
            leadingNone
            className="block md:hidden"
          />
          <Typography
            text={text}
            weight={weight}
            font="grotesk"
            color={color}
            size={textSize}
            leadingNone
            className="hidden md:block"
          />
        </>
      ) : (
        <Typography
          text={text}
          weight={weight}
          font="grotesk"
          color={color}
          size={textSize}
          leadingNone
        />
      )}
      {style === "icon" && icon && (
        <div
          className={classNames(
            getTextColorClass(color),
            "w-20 md:w-[22px] lg:w-24"
          )}
        >
          {icon}
        </div>
      )}
    </>
  )

  if (showGetStartedModal) {
    return (
      <button
        onClick={(e) =>
          handleOnClick({
            e,
            ...sharedButtonClickProps,
            onClick: () => setIsGetStartedModalOpen(true),
          })
        }
        className={buttonClasses}
        disabled={disabled}
        data-testid={dataTestId}
      >
        {buttonContent}
      </button>
    )
  }

  if (linkUrl && mobileLinkUrl) {
    return (
      <>
        <Link
          link={linkUrl}
          onClick={(e) =>
            handleOnClick({ e, linkUrl, ...sharedButtonClickProps })
          }
          className={classNames("hidden md:inline-flex", buttonClasses)}
          queryParams={queryParams}
          data-testid={`${dataTestId}-desktop`}
        >
          {buttonContent}
        </Link>
        <Link
          link={mobileLinkUrl}
          onClick={(e) =>
            handleOnClick({
              e,
              linkUrl: mobileLinkUrl,
              ...sharedButtonClickProps,
            })
          }
          className={classNames("md:hidden", buttonClasses)}
          queryParams={queryParams}
          data-testid={`${dataTestId}-mobile`}
        >
          {buttonContent}
        </Link>
      </>
    )
  } else if (linkUrl && !mobileLinkUrl) {
    return (
      <Link
        link={linkUrl}
        onClick={(e) =>
          handleOnClick({ e, linkUrl, ...sharedButtonClickProps })
        }
        className={buttonClasses}
        queryParams={queryParams}
        data-testid={dataTestId}
      >
        {buttonContent}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={(e) => handleOnClick({ e, ...sharedButtonClickProps })}
      className={buttonClasses}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {buttonContent}
    </button>
  )
}

export default TextButton
